/**
 * V2ScopeBuilderPage — the V2 "Scope Builder" screen.
 *
 * A dense, dark-first form for turning room-by-room damage descriptions into a
 * generated Xactimate scope. Two views toggle via a Segmented control:
 *   - Form (default): claim-context card + per-room cards + generate actions.
 *   - Generated: the returned scope rendered through the reusable
 *     {@link ScopeTreeTable} (full-bleed).
 *
 * DATA TRUTHS (load-bearing):
 *  - The generate request mirrors the classic ScopeBuilderPage payload field
 *    names exactly: `field_notes`, `damage_type`, `property_type` (from claim
 *    type), `jurisdiction` (ZIP), `cat3_toggle`, `helpful_mode`. See
 *    `ScopeGenerationRequest` in web/src/types/index.ts.
 *  - Uses the NON-streaming `BackendAPI.generateScopeEnhanced` (no SSE this
 *    pass).
 *  - `ScopeResponseLineItem` has NO price/total field — scope tree items are
 *    emitted with `total: 0` (the tree-table footer notes that pricing +
 *    grand-total recalculation happen in Xactimate on export). We do NOT
 *    fabricate pricing.
 *  - `xactimate_code` is ONE string → split with `splitXactimateCode` for the
 *    CAT/SEL cells.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import type {
  FieldNote,
  Project,
  RoomFeature,
  RoomGeometry,
  ScopeGenerationRequest,
  ScopeGenerationResponse,
  StructuredAddress,
} from '../../types';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { BackendAPI } from '../../lib/api';
import useProjectStore from '../../stores/projectStore';
import { useScopeEsxExport } from '../../hooks/useScopeEsxExport';
import useAuthStore from '../../stores/authStore';
import CreateProjectForm from '../components/CreateProjectForm';
import { splitXactimateCode } from '../lib/xcode';
import { activitySymbol } from '../lib/semantics';
import {
  ScopeTreeTable,
  type ActionChoice,
  type ScopeMutation,
  type ScopeTreeData,
  type ScopeTreeItem,
  type SelBrowseOption,
  type TreeNode,
} from '../components/ScopeTreeTable';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Pill } from '../components/ui/pill';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Cat3Toggle, cat3LockReason, readStoredCategory, storedCat3Default } from '../components/Cat3Toggle';
import { Segmented } from '../components/ui/segmented';
import { EmptyState } from '../components/ui/empty-state';
import { Banner } from '../components/ui/banner';
import { Modal } from '../components/ui/modal';
import {
  IconZap,
  IconDownload,
  IconPen,
  IconFolder,
  IconPin,
  IconMic,
  IconTrash,
  IconPlus,
  IconWarn,
  IconChevd,
} from '../components/icons';
import { DAMAGE_TYPES, CLAIM_TYPES } from '../../data/roomChecklists';
import RoofInterviewPanel, {
  isRoofPeril,
  mergeRoofIntoScope,
} from '../components/roof/RoofInterviewPanel';

// ================================================================= //
//  scopeToTreeData — ScopeGenerationResponse -> ScopeTreeData          //
// ================================================================= //

/**
 * Slugifies a room name into a stable tree-node id.
 *
 * @param name - The room name.
 * @param idx - Fallback index for blank/duplicate names.
 * @returns A url-safe slug, never empty.
 */
function roomSlug(name: string, idx: number): string {
  const slug = (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `room-${idx + 1}`;
}

/**
 * Adapts a {@link ScopeGenerationResponse} into the {@link ScopeTreeData}
 * shape consumed by {@link ScopeTreeTable}.
 *
 * Mapping:
 *  - rooms -> tree nodes (id = slug(room_name), label = room_name).
 *  - items flattened across rooms with:
 *      area    = room id (ties the row to its tree node for rollups/filtering)
 *      heading = surface (when present) else room_name (grouping header)
 *      cat/sel = splitXactimateCode(xactimate_code)
 *      act     = activitySymbol(activity)
 *      qty     = quantity
 *      unit    = unit
 *      desc    = description
 *      note    = note ?? reasoning (RULE-019 fallback order)
 *      total   = 0 (NO pricing in scope items — never fabricated)
 *
 * @param resp - The generated scope response.
 * @returns The tree-table data model.
 */
export function scopeToTreeData(resp: ScopeGenerationResponse): ScopeTreeData {
  const tree: TreeNode[] = [];
  const items: ScopeTreeItem[] = [];
  let n = 0;

  resp.rooms.forEach((room, idx) => {
    const id = roomSlug(room.room_name, idx);
    tree.push({ id, label: room.room_name || `Room ${idx + 1}` });

    room.items.forEach((it, itemIdx) => {
      const { cat, sel } = splitXactimateCode(it.xactimate_code);
      items.push({
        id: `${id}-${n}`,
        area: id,
        heading: it.surface || room.room_name || `Room ${idx + 1}`,
        n: ++n,
        cat,
        sel,
        act: activitySymbol(it.activity),
        qty: it.quantity,
        unit: it.unit,
        // The API sends the sketch variable on the wire as `variable`;
        // `calc_variable` is the backend-internal name and only appears in
        // older saved-scope dicts. Reading only calc_variable left the CALC
        // column blank on every fresh generation.
        calc:
          (it as { variable?: string | null }).variable ??
          (it as { calc_variable?: string | null }).calc_variable ??
          null,
        // Reason: ScopeResponseLineItem carries no price/total — emit 0 and let
        // the tree-table footer disclaim that pricing happens in Xactimate.
        total: 0,
        desc: it.description,
        // The engine's explanation is a read-only JUSTIFICATION (shown on row
        // click); the user's own note lives in attached_notes (emitted into
        // the ESX as an attached ITEM note) and is restored on reload.
        note: (it as { attached_notes?: string[] }).attached_notes?.[0] || undefined,
        justification: it.note ?? it.reasoning ?? undefined,
        roomIdx: idx,
        itemIdx,
      });
    });
  });

  return {
    name: 'Generated Scope',
    sub: 'AI Estimate Assistant',
    priceList: (resp.metadata?.price_list as string) || '—',
    ref: 'Draft',
    tree,
    items,
  };
}

// ================================================================= //
//  Local form state shapes                                            //
// ================================================================= //

/** A per-room form entry held in local state. */
interface RoomForm {
  id: string;
  roomType: string;
  description: string;
  l: string;
  w: string;
  h: string;
  // NFIP flood intake (rendered only when the flood toggle is on). Kept as
  // strings for the form controls; parsed at request-assembly time. Water
  // category is NOT collected here — an SFIP flood is IICRC Cat-3 by
  // definition, so the backend forces water_category=3 (see scope.py).
  waterDepthIn?: string;
  areaClass?: string; // one of FLOOD_AREA_CLASSES[].value
  // Structured data carried through from app-captured rooms (B-124/B-140), kept
  // for downstream use (per-room sketch generation, B-143). Not all rendered yet.
  roomId?: string;
  geometry?: RoomGeometry;
  features?: RoomFeature[];
}

/** NFIP area classes offered per room; value matches the backend enum. */
const FLOOD_AREA_CLASSES = [
  { value: 'basement', label: 'Basement' },
  { value: 'enclosure_below_lef', label: 'Below-LEF enclosure' },
  { value: 'above_lef', label: 'Above lowest floor (LEF)' },
  { value: 'crawlspace', label: 'Crawlspace' },
  { value: 'garage_attached', label: 'Attached garage' },
] as const;

/** Generates a unique id for a room entry. */
function newRoomId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `room-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** A fresh, empty room form entry. */
function emptyRoom(): RoomForm {
  return {
    id: newRoomId(),
    roomType: '',
    description: '',
    l: '',
    w: '',
    h: '',
    waterDepthIn: '',
    areaClass: 'above_lef',
  };
}

/** Water-quality descriptors offered as toggle chips in the flood section. */
const FLOOD_QUALITY_TERMS = [
  'saltwater',
  'freshwater',
  'sewage',
  'fuel oil',
  'silt',
] as const;

// Water-depth inputs accept a number with an optional unit. The engine keys
// everything off INCHES, so a bare "2 ft" that reaches the request as 2 would
// be silently sent as 2 inches — a real bug. Normalize to inches here, in one
// place, so both the house field, the per-room override, and the request
// builder agree. Returns null (never a guess) when the value can't be parsed.
const DEPTH_FOOT_UNITS = new Set(["'", 'ft', 'ft.', 'feet', 'foot', 'f']);
const DEPTH_INCH_UNITS = new Set(['', '"', 'in', 'in.', 'inch', 'inches']);
export function parseDepthToInches(raw: string): number | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
  if (!m) return null;
  const value = parseFloat(m[1]);
  if (!Number.isFinite(value)) return null;
  const unit = m[2].trim().toLowerCase();
  if (DEPTH_FOOT_UNITS.has(unit)) return value * 12;
  if (DEPTH_INCH_UNITS.has(unit)) return value;
  return null; // unrecognized unit — leave as typed, don't guess
}

type ViewMode = 'form' | 'tree';

/**
 * Maps a project's stored loss/damage descriptor (canonical value or legacy
 * free text like "Hurricane Ian — wind") onto the canonical damage-type list
 * so the form Select can actually prefill. Returns '' when unmappable.
 */
function normalizeDamageType(raw?: string | null): string {
  if (!raw) return '';
  const v = raw.trim().toLowerCase();
  if (DAMAGE_TYPES.some((d) => d.value === v)) return v;
  for (const key of [
    'hurricane',
    'flood',
    'water',
    'wind',
    'hail',
    'smoke',
    'fire',
    'mold',
    'storm',
    'vandalism',
  ] as const) {
    if (v.includes(key)) return key;
  }
  return '';
}

/**
 * Maps a project's stored claim_type onto the canonical CLAIM_TYPES value so the
 * Select prefills. The mobile app stores capitalized labels ("Homeowner"); the
 * options are lowercase ("homeowner"), so a raw value silently showed "Select…".
 * Returns '' when unmappable.
 */
function normalizeClaimType(raw?: string | null): string {
  if (!raw) return '';
  const v = raw.trim().toLowerCase();
  if (CLAIM_TYPES.some((c) => c.value === v)) return v;
  if (v.includes('flood') || v.includes('nfip')) return 'flood';
  if (v.includes('commercial')) return 'commercial';
  if (v.includes('home') || v.includes('owner') || v.includes('residential')) return 'homeowner';
  return '';
}

// ================================================================= //
//  Page                                                               //
// ================================================================= //

/** V2 Scope Builder page. */
export default function V2ScopeBuilderPage() {
  const [view, setView] = useState<ViewMode>('form');

  // --- Claim context ---
  const [address, setAddress] = useState('');
  const [damageType, setDamageType] = useState('');
  // Homeowner is the overwhelmingly common claim type; flood damage flips it
  // automatically below unless the user picked something themselves.
  const [claimType, setClaimType] = useState('homeowner');
  const claimTypeTouchedRef = useRef(false);
  const [zip, setZip] = useState('');
  // Cat-3 starts OFF (storm is not assumed Category 3). Flood and an NFIP claim lock
  // it ON (see cat3LockReason); a stored estimate category of 3 turns it on, 1 or 2
  // keep it off (see storedCat3Default).
  const [cat3, setCat3] = useState(false);
  // NFIP flood claim: enables the water inputs and sends
  // claim_type="flood_nfip" so the backend runs NFIP rule checks (S4).
  const [floodClaim, setFloodClaim] = useState(false);
  // Tracks whether the user manually toggled the NFIP flood claim switch. When
  // untouched, floodClaim auto-derives from the project/damage type; once the
  // user sets it, their choice sticks (a manual untick must not get re-armed).
  const floodClaimTouchedRef = useRef(false);
  // A category stored on the project (3 -> Cat-3 on; 1 or 2 -> off), if any.
  const storedCategoryRef = useRef<number | null>(null);
  const cat3Effective = cat3LockReason(damageType, floodClaim) ? true : cat3; // flood / NFIP lock it on
  // House-level water depth is the PRIMARY input — water seeks its level, so
  // adjusters record one depth for the whole house. Per-room depth is an
  // optional override (split levels / basements). Founder feedback.
  const [houseWaterDepth, setHouseWaterDepth] = useState('');
  // Water quality is described house-wide (no category selector — SFIP flood
  // is IICRC Cat-3 by definition). Selected chips + free text compose one
  // water_quality_desc string that fans out to every room. Founder directive.
  const [waterQualityTerms, setWaterQualityTerms] = useState<string[]>([]);
  const [waterQualityOther, setWaterQualityOther] = useState('');
  const toggleQualityTerm = useCallback((t: string) => {
    setWaterQualityTerms((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }, []);
  // Compose in canonical term order (stable regardless of click order), free
  // text appended last.
  const waterQualityDesc = useMemo(() => {
    const terms = FLOOD_QUALITY_TERMS.filter((t) =>
      waterQualityTerms.includes(t),
    );
    const other = waterQualityOther.trim();
    return [...terms, ...(other ? [other] : [])].join(', ');
  }, [waterQualityTerms, waterQualityOther]);
  const [helpful, setHelpful] = useState(true);
  const [trace, setTrace] = useState(false);
  // Section disclosure state (founder ask: collapsible context/notes cards).
  const [ctxOpen, setCtxOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(true);
  const [roofOpen, setRoofOpen] = useState(true);

  /** Applies an autocomplete selection: full address plus ZIP for jurisdiction. */
  const handleAddressSelect = useCallback((a: StructuredAddress) => {
    setAddress(a.formatted);
    if (a.zip) setZip(a.zip);
  }, []);

  /** Applies a damage-type choice plus its side effects (Cat-3, claim type). */
  const applyDamageType = useCallback((v: string) => {
    setDamageType(v);
    // Flood is locked on by cat3LockReason; every other peril goes back to the
    // stored-category default (off when nothing is stored).
    setCat3(storedCat3Default(storedCategoryRef.current));
    if (v === 'flood' && !claimTypeTouchedRef.current) setClaimType('flood');
    // Auto-derive the NFIP flood claim toggle from the damage type unless the
    // user set it themselves: flood arms it; switching away disarms it (but only
    // when the claim type isn't itself flood — see the claim-type onChange).
    if (!floodClaimTouchedRef.current) {
      if (v === 'flood') setFloodClaim(true);
      else if (claimType !== 'flood') setFloodClaim(false);
    }
  }, [claimType]);

  // --- Per-room entries ---
  const [rooms, setRooms] = useState<RoomForm[]>(() => [emptyRoom()]);

  // --- Generation state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeGenerationResponse | null>(null);
  // Latest roof-scope blob from the roof interview (PRP-ROOF-2 S3). Merged into
  // scope_data.metadata.roof at every save so the ESX export emits a Roof folder.
  // A ref mirrors it so the debounced edits-persist effect reads the current
  // value without re-subscribing on every roof keystroke.
  const [roofScope, setRoofScope] = useState<Record<string, unknown> | null>(null);
  const roofScopeRef = useRef<Record<string, unknown> | null>(null);
  roofScopeRef.current = roofScope;

  // --- Project context (project-centric: a scope belongs to a project) ---
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  const selectedProject = useProjectStore((s) => s.selectedProject);
  const getProjectById = useProjectStore((s) => s.getProjectById);
  const setSelectedProject = useProjectStore((s) => s.setSelectedProject);
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  // --- Captured field notes (voice/text/photo from the project's notes) ---
  const [notesText, setNotesText] = useState('');

  // --- Overall claim notes (house-wide context, e.g. flooring throughout) ---
  const [overallNotes, setOverallNotes] = useState('');

  // --- Draft save state (save-without-generate, B-258) ---
  type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Autosave is gated until the initial draft/notes hydration resolves, so the
  // hydration itself never writes a draft.
  const hydratedRef = useRef(false);
  // JSON of the last payload persisted — autosave no-ops when nothing changed.
  const lastSavedJsonRef = useRef<string | null>(null);
  const draftJsonRef = useRef<string>('');
  // Consumed once right after a draft hydration so re-rendering the just-loaded
  // draft doesn't immediately PUT it back.
  const suppressNextAutosaveRef = useRef(false);
  // Set when the draft fetch finds nothing — keys the captured-notes prefill.
  const [notesPrefillId, setNotesPrefillId] = useState<string | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  // --- ESX export state ---
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const { exportEsx, exporting, error: exportError } = useScopeEsxExport();

  // --- Admin troubleshooting export (form + generated scope as JSON) ---
  const roles = useAuthStore((s) => s.roles);
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');
  const [debugMsg, setDebugMsg] = useState<string | null>(null);

  // --- Line-item edits (remove / desc / note / action / SEL) ------------
  // Keyed by the item's ORIGINAL position ("roomIdx:itemIdx") so keys stay
  // stable across removals; the EFFECTIVE scope (edits applied) is what gets
  // persisted, exported to ESX, and debug-dumped.
  interface ItemPatch {
    desc?: string;
    note?: string;
    act?: string;
    sel?: string;
    unit?: string;
  }
  const [scopeEdits, setScopeEdits] = useState<{
    removed: string[];
    patches: Record<string, ItemPatch>;
  }>({ removed: [], patches: {} });

  const effectiveScope = useMemo(() => {
    if (!scope) return null;
    if (!scopeEdits.removed.length && !Object.keys(scopeEdits.patches).length)
      return scope;
    const removed = new Set(scopeEdits.removed);
    return {
      ...scope,
      rooms: scope.rooms.map((room, ri) => ({
        ...room,
        items: room.items.flatMap((it, ii) => {
          const key = `${ri}:${ii}`;
          if (removed.has(key)) return [];
          const p = scopeEdits.patches[key];
          if (!p) return [it];
          const cat =
            (it as { cat?: string }).cat || splitXactimateCode(it.xactimate_code).cat;
          const next = { ...it } as typeof it & {
            cat?: string;
            sel?: string;
            activity_code?: string;
            calc_variable?: string | null;
            attached_notes?: string[];
          };
          if (p.desc !== undefined) next.description = p.desc;
          if (p.note !== undefined) next.attached_notes = p.note ? [p.note] : [];
          if (p.act !== undefined) {
            next.activity = p.act;
            next.activity_code = p.act;
          }
          if (p.sel !== undefined) {
            next.sel = p.sel;
            next.xactimate_code = `${cat} ${p.sel}`.trim();
            if (p.unit !== undefined && p.unit !== it.unit) {
              next.unit = p.unit;
              // The stamped sketch variable assumed the old unit — drop it
              // rather than emit a mismatched variable calc. Both spellings:
              // `variable` is the wire name, calc_variable the internal one.
              next.calc_variable = null;
              (next as { variable?: string | null }).variable = null;
            }
          }
          return [next];
        }),
      })),
    } as ScopeGenerationResponse;
  }, [scope, scopeEdits]);

  const handleScopeMutate = useCallback((m: ScopeMutation) => {
    const key = `${m.item.roomIdx}:${m.item.itemIdx}`;
    if (m.item.roomIdx == null || m.item.itemIdx == null) return;
    setScopeEdits((prev) => {
      if (m.type === 'remove')
        return prev.removed.includes(key)
          ? prev
          : { ...prev, removed: [...prev.removed, key] };
      if (m.type === 'restore')
        return { ...prev, removed: prev.removed.filter((k) => k !== key) };
      return {
        ...prev,
        patches: { ...prev.patches, [key]: { ...prev.patches[key], ...m.patch } },
      };
    });
  }, []);

  const loadSelectors = useCallback(
    async (cat: string): Promise<SelBrowseOption[]> =>
      (await BackendAPI.listSelectors(cat)).selectors,
    [],
  );
  const loadActions = useCallback(
    async (cat: string, sel: string): Promise<ActionChoice[]> =>
      (await BackendAPI.listActions(cat, sel)).actions,
    [],
  );

  // Debounced persistence of line-item edits: the EFFECTIVE scope replaces the
  // project's saved scope so reloads and Download-as-ESX both see the edits.
  const lastSavedEditsRef = useRef('');
  useEffect(() => {
    const editsJson = JSON.stringify(scopeEdits);
    if (editsJson === lastSavedEditsRef.current) return;
    if (!effectiveScope || !activeProject) return;
    if (!scopeEdits.removed.length && !Object.keys(scopeEdits.patches).length) {
      lastSavedEditsRef.current = editsJson;
      return;
    }
    const t = setTimeout(async () => {
      try {
        // Re-merge the current roof scope so post-generation roof edits persist.
        const toSave = mergeRoofIntoScope(effectiveScope, roofScopeRef.current);
        const saved = await BackendAPI.saveProjectScope(
          activeProject.id,
          toSave,
          'web',
          toSave.rule_trace,
        );
        lastSavedEditsRef.current = editsJson;
        setScopeId((saved?.id as string | undefined) ?? null);
      } catch (err) {
        console.warn('[V2ScopeBuilder] persisting line-item edits failed:', err);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [scopeEdits, effectiveScope, activeProject]);

  // Resolve the active project: an explicit ?project=<id> wins, else the
  // app-wide selected project from the store.
  useEffect(() => {
    let cancelled = false;
    if (projectIdFromUrl) {
      getProjectById(projectIdFromUrl).then((p) => {
        if (!cancelled && p) setActiveProject(p);
      });
    } else {
      setActiveProject(selectedProject);
    }
    return () => {
      cancelled = true;
    };
  }, [projectIdFromUrl, selectedProject, getProjectById]);

  // Re-fill the form from the active project whenever the project IDENTITY
  // changes. The top-bar picker switches projects IN PLACE (this page stays
  // mounted), so the old "fill only when empty" guards kept the PREVIOUS
  // project's values after a switch — address, notes, rooms — which read as
  // "switching the project does nothing below". We track the loaded id so a
  // re-render with the SAME project never clobbers the user's edits.
  const loadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeProject) return;
    if (loadedProjectIdRef.current === activeProject.id) return;
    loadedProjectIdRef.current = activeProject.id;
    setAddress(activeProject.address || '');
    setZip(activeProject.zip || '');
    // Projects usually carry loss_type (set at creation / PDF import); fall
    // back to it when the dedicated damage_type was never set.
    const dt = normalizeDamageType(activeProject.damage_type || activeProject.loss_type);
    setDamageType(dt);
    storedCategoryRef.current = readStoredCategory(activeProject);
    setCat3(storedCat3Default(storedCategoryRef.current));
    claimTypeTouchedRef.current = false;
    setClaimType(normalizeClaimType(activeProject.claim_type) || (dt === 'flood' ? 'flood' : 'homeowner'));
    // A project that reads as a flood loss (damage/loss type OR claim type) arms
    // the NFIP toggle by default; the user can still untick it (which sticks via
    // floodClaimTouchedRef, restored from the draft below).
    floodClaimTouchedRef.current = false;
    setFloodClaim(dt === 'flood' || normalizeClaimType(activeProject.claim_type) === 'flood');
    // Reset per-project working state so the new project starts clean. The notes
    // effect below re-fills notesText from the newly-active project (prev is now
    // empty, so its `prev || block` fills it).
    setNotesText('');
    setOverallNotes('');
    setRooms([emptyRoom()]);
    setScope(null);
    setScopeId(null);
    setView('form');
    setSaveState('idle');
    setSavedAt(null);
    hydratedRef.current = false;
    lastSavedJsonRef.current = null;
    setNotesPrefillId(null);

    const projectId = activeProject.id;

    // Saved draft wins over captured-notes prefill: a draft already absorbed
    // (or superseded) the captured notes when it was written.
    BackendAPI.getScopeDraft(projectId)
      .then((res) => {
        if (loadedProjectIdRef.current !== projectId) return; // stale response
        const d = res?.draft as Record<string, unknown> | undefined;
        if (d && typeof d === 'object' && Object.keys(d).length > 0) {
          if (typeof d.overallNotes === 'string') setOverallNotes(d.overallNotes);
          if (typeof d.notesText === 'string' && d.notesText) setNotesText(d.notesText);
          if (typeof d.damageType === 'string' && d.damageType) setDamageType(d.damageType);
          if (typeof d.claimType === 'string' && d.claimType) setClaimType(d.claimType);
          if (typeof d.zip === 'string' && d.zip) setZip(d.zip);
          if (typeof d.cat3 === 'boolean') setCat3(d.cat3);
          // Only let the draft override the project-derived flood default when
          // the user explicitly touched the toggle. Stale pre-fix drafts saved
          // floodClaim:false without the touched flag; honoring those would
          // wrongly re-disarm flood projects.
          if (d.floodClaimTouched === true && typeof d.floodClaim === 'boolean') {
            floodClaimTouchedRef.current = true;
            setFloodClaim(d.floodClaim);
          }
          if (typeof d.houseWaterDepth === 'string') setHouseWaterDepth(d.houseWaterDepth);
          // Restore water-quality inputs; tolerate old drafts that predate them
          // (and old houseWaterCategory drafts, now ignored).
          if (Array.isArray(d.waterQualityTerms))
            setWaterQualityTerms(
              d.waterQualityTerms.filter((t): t is string => typeof t === 'string'),
            );
          if (typeof d.waterQualityOther === 'string')
            setWaterQualityOther(d.waterQualityOther);
          if (typeof d.helpful === 'boolean') setHelpful(d.helpful);
          const draftRooms = d.rooms;
          if (Array.isArray(draftRooms) && draftRooms.length > 0) {
            setRooms(
              draftRooms.map((r) => ({
                ...emptyRoom(),
                ...(r as Partial<RoomForm>),
                id: newRoomId(),
              })),
            );
          }
          setSavedAt(res?.updated_at ?? null);
          setSaveState('saved');
          suppressNextAutosaveRef.current = true;
        } else {
          setNotesPrefillId(projectId);
        }
        hydratedRef.current = true;
      })
      .catch(() => {
        hydratedRef.current = true;
      });

    // Load the project's saved scope so the "Generated scope" tab reflects
    // reality instead of claiming "No scope yet" (demo bug).
    BackendAPI.getLatestProjectScope(projectId).then((raw) => {
      if (loadedProjectIdRef.current !== projectId || !raw) return;
      const sd = raw.scope_data as ScopeGenerationResponse | undefined;
      if (sd && Array.isArray(sd.rooms)) {
        setScope(sd);
        setScopeId((raw.id as string) ?? null);
      }
    });
  }, [activeProject]);

  // Pre-fill from the project's field notes (voice/text/photo captured in the app).
  // Notes tagged with a room_id (B-124/B-140) group into Room cards — room type,
  // the room's dictations as the damage description, geometry's L/W/H, and the
  // captured features carried through for sketch generation (B-143). Untagged
  // notes fall back to the flat "Captured field notes" block exactly as before
  // (so projects with no app-captured rooms are unchanged). Fetch once on project
  // load; best-effort (failures silent so the form stays usable offline).
  useEffect(() => {
    if (!notesPrefillId) return;
    let cancelled = false;
    BackendAPI.getNotesForProject(notesPrefillId)
      .then((notes: FieldNote[]) => {
        if (cancelled) return;

        const tagged = notes.filter((n) => n.room_id);
        const untagged = notes.filter((n) => !n.room_id);

        // --- Build one Room card per room_id group ---
        const byRoom = new Map<string, FieldNote[]>();
        for (const n of tagged) {
          const key = n.room_id as string;
          const arr = byRoom.get(key) ?? [];
          arr.push(n);
          byRoom.set(key, arr);
        }
        const groupedRooms: RoomForm[] = [];
        byRoom.forEach((groupNotes, roomId) => {
          // The add_room marker note carries room_type + geometry + features.
          const meta =
            groupNotes.find((n) => n.room_geometry || n.room_features || n.room_type) ?? groupNotes[0];
          const primary = meta.room_geometry?.primary ?? {};
          const description = groupNotes
            .filter((n) => {
              const c = n.content?.trim();
              if (!c || c === 'Room created') return false; // drop the auto-marker
              return n.type === 'voice' || n.type === 'text';
            })
            .map((n) => n.content.trim())
            .join('\n\n');
          groupedRooms.push({
            id: newRoomId(),
            roomId,
            roomType: meta.room_type ?? '',
            description,
            l: primary.length != null ? String(primary.length) : '',
            w: primary.width != null ? String(primary.width) : '',
            h: primary.height != null ? String(primary.height) : '',
            geometry: meta.room_geometry ?? undefined,
            features: meta.room_features ?? undefined,
          });
        });
        if (groupedRooms.length > 0) setRooms(groupedRooms);

        // --- Untagged notes → the flat captured-notes block (unchanged behavior) ---
        const block = untagged
          .filter((n) => {
            const c = n.content?.trim();
            if (!c) return false;
            if (c === 'Room created') return false; // auto-marker, no signal
            return n.type === 'voice' || n.type === 'text' || n.type === 'photo';
          })
          .map((n) => {
            const c = n.content.trim();
            return n.type === 'photo' ? `- Photo: ${c}` : `- ${c}`;
          })
          .join('\n');
        if (block) setNotesText((prev) => prev || block);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [notesPrefillId]);

  // ---- Draft autosave (save-without-generate, B-258) ----
  // The serialized form payload; rooms drop their transient UI id.
  const draftPayload = useMemo(
    () => ({
      version: 1,
      overallNotes,
      notesText,
      damageType,
      claimType,
      zip,
      cat3,
      floodClaim,
      // Persist whether the flood toggle was user-set so restore can tell an
      // explicit choice from the project-derived default (see draft restore).
      floodClaimTouched: floodClaimTouchedRef.current,
      houseWaterDepth,
      waterQualityTerms,
      waterQualityOther,
      helpful,
      rooms: rooms.map(({ id: _id, ...rest }) => rest),
    }),
    [
      overallNotes,
      notesText,
      damageType,
      claimType,
      zip,
      cat3,
      floodClaim,
      houseWaterDepth,
      waterQualityTerms,
      waterQualityOther,
      helpful,
      rooms,
    ],
  );
  const draftJson = JSON.stringify(draftPayload);
  draftJsonRef.current = draftJson;

  /** Persists the current form as the project draft (no AI involved). */
  const flushSave = useCallback(async () => {
    const projectId = loadedProjectIdRef.current;
    if (!projectId) return;
    if (draftJsonRef.current === lastSavedJsonRef.current) {
      setSaveState((s) => (s === 'dirty' ? 'saved' : s));
      return;
    }
    const json = draftJsonRef.current;
    setSaveState('saving');
    try {
      const res = await BackendAPI.putScopeDraft(
        projectId,
        JSON.parse(json) as Record<string, unknown>,
      );
      lastSavedJsonRef.current = json;
      if (loadedProjectIdRef.current === projectId) {
        setSavedAt(res.updated_at);
        setSaveState('saved');
      }
    } catch {
      if (loadedProjectIdRef.current === projectId) setSaveState('error');
    }
  }, []);

  // Debounced autosave: any form change marks dirty, then writes 2s after the
  // user stops editing. Gated until hydration so loading a draft can't loop.
  useEffect(() => {
    if (!activeProject || !hydratedRef.current) return;
    if (suppressNextAutosaveRef.current) {
      suppressNextAutosaveRef.current = false;
      lastSavedJsonRef.current = draftJson;
      return;
    }
    if (draftJson === lastSavedJsonRef.current) return;
    setSaveState('dirty');
    const t = setTimeout(() => void flushSave(), 2000);
    return () => clearTimeout(t);
  }, [draftJson, activeProject, flushSave]);

  const updateRoom = useCallback((id: string, patch: Partial<RoomForm>) => {
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const addRoom = useCallback(() => {
    setRooms((prev) => [...prev, emptyRoom()]);
  }, []);

  const removeRoom = useCallback((id: string) => {
    setRooms((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  }, []);

  // ---- Generate gating (founder rule: room type, damage description, and
  // measurements are all required on every room before generation) ----

  /** Human-readable list of what's missing on a room ('' when complete). */
  const roomProblems = useCallback((r: RoomForm): string[] => {
    const problems: string[] = [];
    if (!r.roomType.trim()) problems.push('room type');
    if (!r.description.trim()) problems.push('damage description');
    if (![r.l, r.w, r.h].every((v) => parseFloat(v) > 0)) problems.push('measurements (L/W/H)');
    return problems;
  }, []);

  const missingSummary = useMemo(() => {
    const parts: string[] = [];
    if (!damageType) parts.push('damage type');
    rooms.forEach((r, i) => {
      const p = roomProblems(r);
      if (p.length > 0) parts.push(`Room ${i + 1}: ${p.join(', ')}`);
    });
    return parts;
  }, [damageType, rooms, roomProblems]);

  const canGenerate = missingSummary.length === 0;

  /**
   * Builds the scope-generation request from the current form, mirroring the
   * classic ScopeBuilderPage payload field names, and runs generation.
   */
  const generate = useCallback(
    async () => {
      if (missingSummary.length > 0) {
        setError(`Complete the form before generating — missing: ${missingSummary.join('; ')}.`);
        return;
      }

      const capturedNotes = notesText.trim();
      const overall = overallNotes.trim();
      const roomsWithContent = rooms.filter((r) => r.description.trim().length > 0);

      // Reason: classic concatenates each room's "label: description" into a
      // single field_notes block (see ScopeBuilderPage.handleBatchGenerate). We
      // prepend the house-wide overall notes + the project's captured field
      // notes so both reach scope generation alongside typed room descriptions
      // (the engine treats the leading block as house-wide overview context —
      // e.g. "engineered wood throughout" resolves a room's "replace floor").
      // Structured mode: house-wide notes become the overview (field_notes);
      // each room's description + STRUCTURED dimensions go in its own section so
      // measurements reach the engine deterministically and drive quantities
      // (the old flat path crammed dims into text as "(L×W×H)", which the LLM
      // re-parse lost — leaving every quantity blank).
      const num = (v: string): number | undefined => {
        const f = parseFloat(v);
        return Number.isFinite(f) && f > 0 ? f : undefined;
      };
      const roomSections = roomsWithContent.map((r) => {
        const label = r.roomType.trim() || 'Room';
        const dims = [r.l, r.w, r.h].some((v) => v.trim())
          ? ` (L ${r.l || '—'} × W ${r.w || '—'} × H ${r.h || '—'} ft)`
          : '';
        const measurements =
          num(r.l) || num(r.w) || num(r.h)
            ? { length: num(r.l), width: num(r.w), height: num(r.h) }
            : undefined;
        // NFIP flood intake: only attach when the flood toggle is on
        // (fail-closed — the backend never sees these on a standard claim).
        // Depth defaults to the house-level value; a per-room entry overrides
        // it. Water category is NOT sent — the backend forces Cat-3 on armed
        // flood claims. Quality is house-wide and fans out to every room.
        let flood: {
          water_depth_interior_in?: number;
          water_quality_desc?: string;
          area_class?: string;
        } = {};
        if (floodClaim) {
          const roomDepth = parseDepthToInches(r.waterDepthIn ?? '');
          const houseDepth = parseDepthToInches(houseWaterDepth);
          const depth = roomDepth ?? houseDepth;
          flood = {
            ...(depth != null && { water_depth_interior_in: depth }),
            ...(waterQualityDesc && { water_quality_desc: waterQualityDesc }),
            area_class: r.areaClass || 'above_lef',
          };
        }
        return {
          room: label,
          // B-7364: the phone room's id, so the saved scope matches the floor plan by id.
          ...(r.roomId && { room_id: r.roomId }),
          // Keep the readable dims suffix as context; measurements above are
          // the authoritative source the engine computes quantities from.
          text: `${r.description.trim()}${dims}`,
          ...(measurements && { measurements }),
          // App-captured geometry (L-shape second rect, doors/windows) drives
          // ESX sketch openings/adjacency and true L-shape quantities.
          ...(r.geometry && { geometry: r.geometry }),
          // App-captured features (cabinets etc.) are directive — each
          // generates scope activities resolved via the codes service.
          ...(r.features?.length && { features: r.features }),
          ...flood,
        };
      });

      const fieldNotes = [
        overall && `Overall claim notes (house-wide):\n${overall}`,
        capturedNotes && `Captured field notes:\n${capturedNotes}`,
      ]
        .filter(Boolean)
        .join('\n\n');

      const request: ScopeGenerationRequest = {
        field_notes: fieldNotes,
        room_sections: roomSections,
        damage_type: damageType,
        ...(claimType && { property_type: claimType }),
        // Flood arming. When the user explicitly UNticked the flood toggle
        // (touched + false) send claim_type:'standard' so the backend's
        // explicit-wins rule disarms — otherwise a flood-ish damage_type /
        // property_type could silently re-arm the NFIP pack. When untouched and
        // false, omit it and let the backend auto-detect.
        ...(floodClaim
          ? { claim_type: 'flood_nfip' }
          : floodClaimTouchedRef.current
            ? { claim_type: 'standard' }
            : {}),
        ...(zip.trim() && { jurisdiction: zip.trim() }),
        cat3_toggle: cat3Effective,
        ...(helpful && { helpful_mode: true }),
        ...(trace && { debug_trace: true }),
      };

      setLoading(true);
      setError(null);
      try {
        // Flush the form draft first so what generated is what's saved.
        await flushSave();
        const result = await BackendAPI.generateScopeEnhanced(request);
        // Carry the roof interview's scope into scope_data.metadata.roof so both
        // the rendered scope and the persisted copy feed the ESX Roof folder.
        const withRoof = mergeRoofIntoScope(result, roofScopeRef.current);
        setScope(withRoof);
        setScopeEdits({ removed: [], patches: {} }); // fresh generation, no edits
        setScopeId(null);
        setView('tree');

        // Project-centric: persist the generated scope to the active project so
        // Export downloads the saved scope and reopening the project restores it.
        if (activeProject) {
          try {
            const saved = await BackendAPI.saveProjectScope(
              activeProject.id,
              withRoof,
              'web',
              withRoof.rule_trace,
            );
            setScopeId((saved?.id as string | undefined) ?? null);
          } catch (saveErr) {
            // Non-fatal: scope still renders; Export will save-then-export.
            console.warn('[V2ScopeBuilder] auto-save to project failed:', saveErr);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Scope generation failed.');
      } finally {
        setLoading(false);
      }
    },
    [
      missingSummary,
      rooms,
      notesText,
      overallNotes,
      damageType,
      claimType,
      zip,
      cat3Effective,
      floodClaim,
      houseWaterDepth,
      waterQualityDesc,
      helpful,
      trace,
      activeProject,
      flushSave,
    ],
  );

  /** Generate entry point: confirms first when a scope already exists
   *  (generation costs AI credits — founder wants ~once per claim). */
  const requestGenerate = useCallback(() => {
    if (!canGenerate) {
      setError(`Complete the form before generating — missing: ${missingSummary.join('; ')}.`);
      return;
    }
    setError(null);
    if (scope) {
      setConfirmRegen(true);
      return;
    }
    void generate();
  }, [canGenerate, missingSummary, scope, generate]);

  // Export the generated scope to an importable .ESX. Project-centric: requires
  // a project. When none is active, prompt to create one, then export.
  const handleExport = useCallback(async () => {
    if (!effectiveScope) return;
    if (!activeProject) {
      setShowCreateProject(true);
      return;
    }
    // With unsaved line-item edits, force a save-then-export of the EDITED
    // scope (passing the stale scopeId would export the pre-edit save).
    const hasEdits =
      scopeEdits.removed.length > 0 || Object.keys(scopeEdits.patches).length > 0;
    const res = await exportEsx({
      scopeId: hasEdits ? undefined : (scopeId ?? undefined),
      projectId: activeProject.id,
      scopeData: mergeRoofIntoScope(effectiveScope, roofScopeRef.current),
      projectNameOverride: activeProject.owner_name || undefined,
    });
    if (res) setScopeId(res.scopeId);
  }, [effectiveScope, scopeEdits, activeProject, scopeId, exportEsx]);

  // Admin/testing: dump the FORM CONTENTS + GENERATED SCOPE as JSON —
  // copied to the clipboard and downloaded — so a problem generation can be
  // pasted straight into a debugging session.
  const handleDebugExport = useCallback(async () => {
    const dump = {
      kind: 'scope-builder-debug-export',
      exported_at: new Date().toISOString(),
      project: activeProject
        ? { id: activeProject.id, name: activeProject.owner_name ?? null }
        : null,
      form: {
        address,
        damage_type: damageType,
        claim_type: claimType,
        zip,
        cat3,
        flood_claim: floodClaim,
        ...(floodClaim && {
          house_water_depth_in: houseWaterDepth,
          water_quality_desc: waterQualityDesc,
        }),
        helpful_mode: helpful,
        overall_claim_notes: overallNotes,
        field_notes: notesText,
        rooms: rooms.map((r) => ({
          room_type: r.roomType,
          description: r.description,
          measurements_ft: { l: r.l, w: r.w, h: r.h },
          ...(floodClaim && {
            flood: {
              water_depth_in: r.waterDepthIn,
              area_class: r.areaClass,
            },
          }),
        })),
      },
      generated_scope: effectiveScope,
      line_item_edits: scopeEdits,
    };
    const json = JSON.stringify(dump, null, 2);
    let copied = false;
    try {
      await navigator.clipboard.writeText(json);
      copied = true;
    } catch {
      // Clipboard can be denied — the file download below still lands.
    }
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scope-debug-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setDebugMsg(
      copied
        ? 'Debug export copied to clipboard and downloaded as JSON.'
        : 'Debug export downloaded as JSON (clipboard unavailable).',
    );
    window.setTimeout(() => setDebugMsg(null), 6000);
  }, [
    activeProject,
    address,
    damageType,
    claimType,
    zip,
    cat3,
    floodClaim,
    houseWaterDepth,
    waterQualityDesc,
    helpful,
    overallNotes,
    notesText,
    rooms,
    scope,
  ]);

  // After creating a project from the no-project export flow: adopt it as the
  // active/selected project, then save-then-export immediately.
  const handleProjectCreated = useCallback(
    async (project: Project) => {
      setShowCreateProject(false);
      setActiveProject(project);
      setSelectedProject(project);
      if (!effectiveScope) return;
      const res = await exportEsx({
        projectId: project.id,
        scopeData: effectiveScope,
        projectNameOverride: project.owner_name || undefined,
      });
      if (res) setScopeId(res.scopeId);
    },
    [effectiveScope, exportEsx, setSelectedProject],
  );

  return (
    <div
      className={cn('mx-auto', view === 'tree' ? 'max-w-none' : 'max-w-[1080px]')}
      style={{ padding: 'var(--s5, 24px)' }}
    >
      {/* ---- page head ---- */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[19px] font-semibold text-[var(--tx-hi)]">Scope Builder</h1>
          <p className="mt-1 text-[12.5px] text-[var(--tx-lo)]">
            Describe damage room-by-room or all at once. Use your device&apos;s dictation to speak
            into any field.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SaveStatus state={saveState} savedAt={savedAt} onRetry={() => void flushSave()} />
          <Segmented<ViewMode>
            value={view}
            onValueChange={setView}
            options={[
              { value: 'form', label: 'Form', icon: <IconPen /> },
              { value: 'tree', label: 'Generated scope', icon: <IconFolder /> },
            ]}
          />
          <Button
            variant="secondary"
            onClick={() => void flushSave()}
            disabled={!activeProject || saveState === 'saving'}
            title={activeProject ? 'Save the form without generating (free)' : 'Select a project to save'}
          >
            Save
          </Button>
          {view === 'tree' && scope ? (
            // Once a scope is generated, the primary action is getting it into
            // Xactimate — regenerating lives on the Form view.
            <Button
              variant="primary"
              onClick={() => void handleExport()}
              disabled={exporting}
              title="Download the generated scope as an importable .ESX"
            >
              <IconDownload />
              {exporting ? 'Preparing ESX…' : 'Download as ESX'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={requestGenerate}
              disabled={loading || !canGenerate}
              title={
                canGenerate
                  ? 'Generate the scope (uses AI credits)'
                  : `Missing: ${missingSummary.join('; ')}`
              }
            >
              <IconZap />
              {loading ? 'Generating…' : 'Generate scope'}
            </Button>
          )}
        </div>
      </div>

      {/* ---- error banner ---- */}
      {error && (
        <Banner tone="warn" icon={<IconWarn />} className="mb-4">
          {error}
        </Banner>
      )}
      {exportError && (
        <Banner tone="warn" icon={<IconWarn />} className="mb-4">
          Export failed: {exportError}
        </Banner>
      )}
      {debugMsg && (
        <Banner tone="info" className="mb-4">
          {debugMsg}
        </Banner>
      )}

      {/* ---- Generated view ---- */}
      {view === 'tree' &&
        (scope && scope.rooms.length > 0 ? (
          <Card className="h-[calc(100vh-200px)] min-h-[600px] overflow-hidden !p-0">
            <ScopeTreeTable
              data={scopeToTreeData(scope)}
              onDebugExport={isAdmin ? handleDebugExport : undefined}
              onMutate={handleScopeMutate}
              loadSelectors={loadSelectors}
              loadActions={loadActions}
            />
          </Card>
        ) : (
          <Card className="py-2">
            <EmptyState
              icon={<IconFolder />}
              title="No scope yet"
              description="Generate a scope to preview it here."
              action={
                <Button variant="primary" onClick={() => setView('form')}>
                  <IconPen />
                  Back to form
                </Button>
              }
            />
          </Card>
        ))}

      {/* ---- Form view ---- */}
      {view === 'form' && (
        <>
          {/* claim context */}
          <Card className="p-[18px] mb-[14px]">
            <SectionHeader open={ctxOpen} onToggle={() => setCtxOpen((o) => !o)}>
              Claim context
            </SectionHeader>
            {ctxOpen && (
            <>
            <div className="grid gap-[14px] grid-cols-[1.4fr_1fr_1fr]">
              <Field className="col-span-full" label="Property address">
                <div className="relative">
                  <span className="absolute left-[11px] top-1/2 -translate-y-1/2 text-[var(--tx-faint)] [&_svg]:w-[15px] [&_svg]:h-[15px]">
                    <IconPin />
                  </span>
                  <AddressAutocomplete
                    key={activeProject?.id ?? 'no-project'}
                    defaultValue={activeProject?.address ?? address}
                    onSelect={handleAddressSelect}
                    placeholder="Start typing an address to auto-fill ZIP…"
                    className="h-[34px] pl-[32px] rounded-[var(--r-md)] bg-[var(--bg-inset)] border-[var(--bd)] text-[length:13px] text-[color:var(--tx-hi)] placeholder:text-[color:var(--tx-faint)] focus:ring-0 focus:border-[var(--accent-line)] focus:shadow-[0_0_0_3px_var(--accent-soft)]"
                  />
                </div>
                <Hint>Selecting an address auto-fills the ZIP for jurisdiction lookup.</Hint>
              </Field>

              <Field
                label={
                  <>
                    Damage type
                    <span className="text-[var(--bad)] ml-[2px]">*</span>
                  </>
                }
              >
                <Select value={damageType} onChange={(e) => applyDamageType(e.target.value)}>
                  <option value="">Select damage type…</option>
                  {DAMAGE_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </Select>
                <Hint>Shapes rules applied and code suggestions.</Hint>
              </Field>

              <Field label="Claim type">
                <Select
                  value={claimType}
                  onChange={(e) => {
                    claimTypeTouchedRef.current = true;
                    setClaimType(e.target.value);
                    // Picking Flood / NFIP arms the toggle unless the user
                    // already set it themselves.
                    if (e.target.value === 'flood' && !floodClaimTouchedRef.current) {
                      setFloodClaim(true);
                    }
                  }}
                >
                  <option value="">Select…</option>
                  <option value="homeowner">Homeowner</option>
                  <option value="flood">Flood / NFIP</option>
                  <option value="commercial">Commercial</option>
                </Select>
                <Hint>Filters coverage rules.</Hint>
              </Field>

              <Field label="ZIP code">
                <Input
                  placeholder="e.g. 33904"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  inputMode="numeric"
                />
                <Hint>Jurisdiction-specific building code.</Hint>
              </Field>
            </div>

            <div className="h-px bg-[var(--bd-subtle)] my-4" />

            <div className="flex gap-8 flex-wrap items-start">
              <Cat3Toggle damageType={damageType} floodClaim={floodClaim} cat3={cat3} set={setCat3} />
              <ToggleField
                on={floodClaim}
                set={(v) => {
                  // A manual toggle sticks: mark it touched so the auto-derive
                  // logic and draft restore stop overriding the user's choice.
                  floodClaimTouchedRef.current = true;
                  setFloodClaim(v);
                }}
                label="NFIP flood claim"
                hint="Runs NFIP rule checks and adds water depth/category inputs."
              />
              <ToggleField
                on={helpful}
                set={setHelpful}
                label="Helpful Mode"
                hint="Show alternatives & which rule fired per item."
              />
              <ToggleField
                on={trace}
                set={setTrace}
                label="Show pipeline trace"
                hint="Admin diagnostics for each generation step."
              />
            </div>

            {floodClaim && (
              <>
                <div className="h-px bg-[var(--bd-subtle)] my-4" />
                <div className="grid gap-4 grid-cols-[160px_1fr] items-start">
                  <Field label="Water depth (in)">
                    <Input
                      className="text-right font-[var(--font-mono)]"
                      value={houseWaterDepth}
                      onChange={(e) => setHouseWaterDepth(e.target.value)}
                      onBlur={(e) => {
                        const inches = parseDepthToInches(e.target.value);
                        if (inches != null) setHouseWaterDepth(String(inches));
                      }}
                      inputMode="decimal"
                      placeholder="e.g. 24"
                      aria-label="House water depth (in)"
                    />
                    <Hint>Whole-house depth; override per room only if it differs.</Hint>
                  </Field>
                  <Field label="Water quality">
                    <div className="flex flex-wrap gap-1.5">
                      {FLOOD_QUALITY_TERMS.map((t) => {
                        const on = waterQualityTerms.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            role="checkbox"
                            aria-checked={on}
                            aria-label={t}
                            onClick={() => toggleQualityTerm(t)}
                            className={cn(
                              'px-2.5 py-1 rounded-full text-[12px] border capitalize transition-colors',
                              on
                                ? 'bg-[var(--accent)] text-[var(--accent-tx)] border-[var(--accent)] font-medium'
                                : 'bg-[var(--bg-elev)] text-[var(--tx-mid)] border-[var(--bd-subtle)] hover:border-[var(--bd)]',
                            )}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      className="mt-2"
                      value={waterQualityOther}
                      onChange={(e) => setWaterQualityOther(e.target.value)}
                      placeholder="Other / details"
                      aria-label="Water quality details"
                    />
                    <Hint>SFIP flood is IICRC Cat-3 by definition; describe the water instead.</Hint>
                  </Field>
                </div>
              </>
            )}

            <div className="h-px bg-[var(--bd-subtle)] my-4" />

            <Field label="Overall claim notes">
              <Textarea
                className="min-h-[72px]"
                rows={3}
                value={overallNotes}
                onChange={(e) => setOverallNotes(e.target.value)}
                placeholder="House-wide context, e.g. “Tree fell on the rear over the living room; engineered wood flooring throughout except kitchen/baths (tile).”"
              />
              <Hint>
                Applies to every room — “replace the floor” in a bedroom resolves against the
                flooring described here.
              </Hint>
            </Field>
            </>
            )}
          </Card>

          {/* roof interview — only for roof-relevant perils (hail/wind/hurricane/
              storm/fire). Collapse HIDES rather than unmounts: the panel owns the
              typed measurements + pending roof scope, and unmounting would clear
              both (onRoofScope(null) cleanup). Switching damage type away still
              unmounts and clears — that's the intended reset. */}
          {isRoofPeril(damageType) && (
            <Card className="p-[18px] mb-[14px]" data-testid="roof-section">
              <SectionHeader open={roofOpen} onToggle={() => setRoofOpen((o) => !o)}>
                Roof
              </SectionHeader>
              <div className={roofOpen ? undefined : 'hidden'}>
                <RoofInterviewPanel zip={zip} onRoofScope={setRoofScope} />
              </div>
            </Card>
          )}

          {/* captured field notes (only when a project is active) */}
          {activeProject && (
            <Card className="p-[18px] mb-[14px]">
              <SectionHeader open={notesOpen} onToggle={() => setNotesOpen((o) => !o)}>
                Captured field notes
              </SectionHeader>
              {notesOpen && (
              <Field label="Field notes">
                <Textarea
                  className="min-h-[120px]"
                  rows={5}
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Field notes captured on this project will appear here…"
                />
                <Hint>
                  Voice &amp; field notes captured on this project. Edit freely — this text is
                  included when you generate the scope.
                </Hint>
              </Field>
              )}
            </Card>
          )}

          {/* per-room cards */}
          {rooms.map((room, i) => (
            <Card key={room.id} className="p-[18px] mb-3">
              <div className="flex items-center gap-[10px] mb-[14px]">
                <Pill kind="accent">Room {i + 1}</Pill>
                <span className="font-semibold text-[14px] text-[var(--tx-hi)]">
                  {room.roomType || 'Untitled room'}
                </span>
                {roomProblems(room).length > 0 && (
                  <span className="text-[11px] text-[var(--warn,#d9a23b)]">
                    Needs {roomProblems(room).join(', ')}
                  </span>
                )}
                <div className="flex-1" />
                {rooms.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-7 px-0 justify-center"
                    title="Remove room"
                    onClick={() => removeRoom(room.id)}
                  >
                    <IconTrash />
                  </Button>
                )}
              </div>
              <div className="grid gap-4 grid-cols-[1.6fr_1fr]">
                <Field label="Damage description">
                  <Textarea
                    className="min-h-[96px]"
                    value={room.description}
                    onChange={(e) => updateRoom(room.id, { description: e.target.value })}
                    placeholder="Describe the damage in this room…"
                  />
                  <Hint className="flex items-center gap-[6px]">
                    <span className="[&_svg]:w-[13px] [&_svg]:h-[13px]">
                      <IconMic />
                    </span>
                    Tap to dictate, or type freely.
                  </Hint>
                </Field>
                <div>
                  <Field label="Room type" className="mb-3">
                    <Input
                      value={room.roomType}
                      onChange={(e) => updateRoom(room.id, { roomType: e.target.value })}
                      placeholder="e.g. Kitchen"
                    />
                  </Field>
                  <label className="block text-[11.5px] font-[550] text-[var(--tx-mid)] mb-[6px]">
                    Measurements (ft)
                  </label>
                  <div className="grid gap-2 grid-cols-3">
                    {(
                      [
                        ['L', room.l, (v: string) => updateRoom(room.id, { l: v })],
                        ['W', room.w, (v: string) => updateRoom(room.id, { w: v })],
                        ['H', room.h, (v: string) => updateRoom(room.id, { h: v })],
                      ] as const
                    ).map(([k, v, set]) => (
                      <div key={k} className="relative">
                        <Input
                          className="pl-[26px] text-right font-[var(--font-mono)]"
                          value={v}
                          onChange={(e) => set(e.target.value)}
                          inputMode="decimal"
                          aria-label={`${k} (ft)`}
                        />
                        <span className="absolute left-[9px] top-[9px] text-[11px] text-[var(--tx-faint)] font-semibold">
                          {k}
                        </span>
                      </div>
                    ))}
                  </div>
                  {floodClaim && (
                    <div className="mt-3">
                      <label className="block text-[11.5px] font-[550] text-[var(--tx-mid)] mb-[6px]">
                        NFIP flood <span className="text-[var(--tx-faint)] font-normal">(depth overrides the house value)</span>
                      </label>
                      <div className="grid gap-2 grid-cols-2">
                        <Input
                          className="text-right font-[var(--font-mono)]"
                          value={room.waterDepthIn ?? ''}
                          onChange={(e) =>
                            updateRoom(room.id, { waterDepthIn: e.target.value })
                          }
                          onBlur={(e) => {
                            const inches = parseDepthToInches(e.target.value);
                            if (inches != null)
                              updateRoom(room.id, { waterDepthIn: String(inches) });
                          }}
                          inputMode="decimal"
                          placeholder={houseWaterDepth ? `${houseWaterDepth} (house)` : 'Depth (in)'}
                          aria-label="Water depth override (in)"
                        />
                        <Select
                          value={room.areaClass ?? 'above_lef'}
                          onChange={(e) =>
                            updateRoom(room.id, { areaClass: e.target.value })
                          }
                          aria-label="Area class"
                        >
                          {FLOOD_AREA_CLASSES.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}

          {/* add another room */}
          <Button
            variant="secondary"
            onClick={addRoom}
            className="w-full justify-center h-10 border-dashed"
          >
            <IconPlus />
            Add another room
          </Button>

          {/* bottom actions */}
          <div className="flex items-center justify-end gap-[10px] mt-4">
            <SaveStatus state={saveState} savedAt={savedAt} onRetry={() => void flushSave()} />
            <Button
              variant="secondary"
              onClick={() => void flushSave()}
              disabled={!activeProject || saveState === 'saving'}
              title={
                activeProject ? 'Save the form without generating (free)' : 'Select a project to save'
              }
            >
              Save
            </Button>
            <Button
              variant="primary"
              onClick={requestGenerate}
              disabled={loading || !canGenerate}
              title={
                canGenerate
                  ? 'Generate the scope (uses AI credits)'
                  : `Missing: ${missingSummary.join('; ')}`
              }
            >
              <IconZap />
              {loading ? 'Generating…' : 'Generate scope'}
            </Button>
          </div>
        </>
      )}

      {confirmRegen && (
        <Modal
          label="Confirm regenerate"
          title="Generate a new scope?"
          subtitle="This project already has a generated scope."
          onClose={() => setConfirmRegen(false)}
          testId="regen-confirm"
          className="max-w-md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmRegen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirmRegen(false);
                  void generate();
                }}
              >
                <IconZap />
                Generate again
              </Button>
            </>
          }
        >
          <span className="text-[12.5px] text-[var(--tx-mid)]">
            Generating again uses AI credits and replaces the current scope as the latest (the
            previous version stays in the project&apos;s scope history).
          </span>
        </Modal>
      )}

      {showCreateProject && (
        <CreateProjectForm
          title="Create a project to export"
          subtitle="A scope belongs to a project. Create one to save and export this scope."
          submitLabel="Create & export"
          initialValues={{
            ...(zip.trim() ? { zip: zip.trim() } : {}),
            ...(claimType ? { claim_type: claimType } : {}),
            ...(damageType ? { loss_type: damageType } : {}),
          }}
          onClose={() => setShowCreateProject(false)}
          onCreated={handleProjectCreated}
        />
      )}
    </div>
  );
}

// ================================================================= //
//  Small local presentational helpers                                 //
// ================================================================= //

/** Compact draft-save status indicator ("Saving… / Saved 2:14 PM / Retry"). */
function SaveStatus({
  state,
  savedAt,
  onRetry,
}: {
  state: 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
  savedAt: string | null;
  onRetry: () => void;
}) {
  if (state === 'idle') return null;
  if (state === 'error') {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="text-[11.5px] text-[var(--bad)] underline-offset-2 hover:underline"
      >
        Save failed — retry
      </button>
    );
  }
  let label = 'Unsaved changes';
  if (state === 'saving') label = 'Saving…';
  if (state === 'saved') {
    // Backend timestamps are naive UTC isoformat — pin the zone so the local
    // wall-clock renders correctly.
    const iso =
      savedAt && !/Z$|[+-]\d\d:\d\d$/.test(savedAt) ? `${savedAt}Z` : savedAt;
    const t = iso ? new Date(iso) : null;
    label =
      t && !Number.isNaN(t.getTime())
        ? `Saved ${t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : 'Saved';
  }
  return <span className="whitespace-nowrap text-[11.5px] text-[var(--tx-faint)]">{label}</span>;
}

/** Clickable disclosure header for a collapsible card section. */
function SectionHeader({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={cn(
        'flex w-full items-center gap-1.5 text-left text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)]',
        'hover:text-[var(--tx-mid)] [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:transition-transform',
        open ? 'pb-3' : 'pb-0',
      )}
    >
      <IconChevd className={cn(!open && '-rotate-90')} />
      {children}
    </button>
  );
}

/** A labeled form field wrapper (label above control). */
function Field({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-[6px]', className)}>
      <label className="text-[11.5px] font-[550] text-[var(--tx-mid)]">{label}</label>
      {children}
    </div>
  );
}

/** Small helper/hint text under a field. */
function Hint({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('text-[11px] text-[var(--tx-faint)]', className)}>{children}</div>;
}

/** A switch + label + hint row used in the claim-context card. */
function ToggleField({
  on,
  set,
  label,
  hint,
}: {
  on: boolean;
  set: (_v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex gap-[10px] items-start cursor-pointer">
      <Switch checked={on} onCheckedChange={set} className="mt-px" />
      <span>
        <span className="text-[12.5px] font-[530] text-[var(--tx-hi)]">{label}</span>
        {hint && <span className="block text-[11px] text-[var(--tx-faint)]">{hint}</span>}
      </span>
    </label>
  );
}
